/**
 * Roughly ten weeks of a 500-employee venue — comfortably above any single load, so the days a
 * write can target are never the ones evicted.
 */
const DEFAULT_MAX_ENTRIES = 50_000;
/**
 * In-memory index of the time entries the session has seen, bucketed per employee and day. The
 * write path reads an entry's same-day siblings from here (auto-break shift counts, auto-meal
 * deduplication) instead of requiring callers to supply them.
 *
 * Buckets are held least-recently-used first and evicted once the entry count exceeds the cap.
 * Evicting is safe because a day can only be written to while it is loaded, and loading re-indexes
 * it.
 */
export class TimeEntryDayIndex {
    constructor(_maxEntries = DEFAULT_MAX_ENTRIES) {
        this._maxEntries = _maxEntries;
        this._buckets = new Map();
        this._entries = new Map();
    }
    /** Upserts entries by id; deleted or unassigned entries are evicted instead. */
    put(entries) {
        for (const entry of entries) {
            if (!entry.id) {
                continue;
            }
            this._forget(entry.id);
            if (entry.deleted || !entry.employeeId) {
                continue;
            }
            const key = this._key(entry.employeeId, entry.date);
            let bucket = this._buckets.get(key);
            if (bucket) {
                this._buckets.delete(key);
            }
            else {
                bucket = new Set();
            }
            this._buckets.set(key, bucket);
            bucket.add(entry);
            this._entries.set(entry.id, { key, entry });
        }
        this._evict();
    }
    /** Drops the given entries from the index. */
    remove(entries) {
        for (const entry of entries) {
            if (entry.id) {
                this._forget(entry.id);
            }
        }
    }
    /** The known same-day entries of the given entries' employees, excluding the entries themselves. */
    contextFor(entries) {
        const context = new Set();
        for (const entry of entries) {
            if (!entry.employeeId) {
                continue;
            }
            const key = this._key(entry.employeeId, entry.date);
            const bucket = this._buckets.get(key);
            if (!bucket) {
                continue;
            }
            this._buckets.delete(key);
            this._buckets.set(key, bucket);
            for (const other of bucket) {
                context.add(other);
            }
        }
        return [...context].filter((other) => !entries.includes(other) && !entries.some((e) => e.id && e.id === other.id));
    }
    clear() {
        this._buckets.clear();
        this._entries.clear();
    }
    _forget(id) {
        const existing = this._entries.get(id);
        if (!existing) {
            return;
        }
        const bucket = this._buckets.get(existing.key);
        bucket?.delete(existing.entry);
        if (bucket?.size === 0) {
            this._buckets.delete(existing.key);
        }
        this._entries.delete(id);
    }
    _evict() {
        while (this._entries.size > this._maxEntries) {
            const oldest = this._buckets.keys().next();
            if (oldest.done) {
                return;
            }
            for (const entry of this._buckets.get(oldest.value) ?? []) {
                if (entry.id) {
                    this._entries.delete(entry.id);
                }
            }
            this._buckets.delete(oldest.value);
        }
    }
    _key(employeeId, date) {
        return `${employeeId}:${date}`;
    }
}
//# sourceMappingURL=time-entry-index.js.map